export type ChartDataset = {
    label: string;
    data: number[];
};

export type VisualizationTable = {
    type: 'table';
    title?: string;
    columns: string[];
    data: Record<string, string | number>[];
};

export type VisualizationBarChart = {
    type: 'bar_chart';
    title?: string;
    labels: string[];
    datasets: ChartDataset[];
};

export type VisualizationLineChart = {
    type: 'line_chart';
    title?: string;
    labels: string[];
    datasets: ChartDataset[];
};

export type ChatVisualization =
    | VisualizationTable
    | VisualizationBarChart
    | VisualizationLineChart;

const CHART_COLORS = ['#0058A8', '#1C8BF1', '#49A2F4', '#6774A1', '#192B68'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const toNumber = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const isNumericValue = (value: unknown) =>
    typeof value === 'number' ||
    (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value)));

const normalizeVisualizationType = (type: unknown) => {
    const normalized = String(type ?? '').toLowerCase().replace(/[\s-]/g, '_');
    if (normalized === 'bar_chart' || normalized === 'bar' || normalized === 'barchart') {
        return 'bar_chart' as const;
    }
    if (normalized === 'line_chart' || normalized === 'line' || normalized === 'linechart') {
        return 'line_chart' as const;
    }
    if (normalized === 'table') {
        return 'table' as const;
    }
    return null;
};

const normalizeDatasets = (
    datasets: unknown,
    fallbackLabel: string,
): ChartDataset[] | null => {
    if (!Array.isArray(datasets) || datasets.length === 0) return null;

    const normalized = datasets
        .map((dataset, index) => {
            if (!isRecord(dataset) || !Array.isArray(dataset.data)) return null;
            return {
                label: String(dataset.label ?? `${fallbackLabel} ${index + 1}`),
                data: dataset.data.map(toNumber),
            };
        })
        .filter((dataset): dataset is ChartDataset => dataset !== null);

    return normalized.length > 0 ? normalized : null;
};

const normalizeRowBasedChart = (
    rows: unknown[],
    type: 'bar_chart' | 'line_chart',
): VisualizationBarChart | VisualizationLineChart | null => {
    if (!rows.every(isRecord) || rows.length === 0) return null;

    const firstRow = rows[0];
    const keys = Object.keys(firstRow);
    const labelKey =
        ['label', 'team', 'week', 'name', 'category', 'x'].find((key) => keys.includes(key)) ??
        keys[0];
    const valueKeys = keys.filter((key) => key !== labelKey);

    if (!labelKey || valueKeys.length === 0) return null;

    const labels = rows.map((row) => String(row[labelKey] ?? ''));
    const datasets = valueKeys.map((valueKey) => ({
        label: valueKey,
        data: rows.map((row) => toNumber(row[valueKey])),
    }));

    return {
        type,
        labels,
        datasets,
    };
};

/** e.g. data: { "Mental Wellbeing": { "Q3 2025": 71, "Q4 2025": 71 } } */
const normalizeSeriesMapChart = (
    data: Record<string, unknown>,
    type: 'bar_chart' | 'line_chart',
    title?: string,
): VisualizationBarChart | VisualizationLineChart | null => {
    const seriesEntries = Object.entries(data).flatMap(([seriesName, points]) =>
        isRecord(points) ? [[seriesName, points] as const] : [],
    );
    if (seriesEntries.length === 0) return null;

    const labels: string[] = [];
    const labelSet = new Set<string>();

    for (const [, points] of seriesEntries) {
        for (const label of Object.keys(points)) {
            if (!labelSet.has(label)) {
                labelSet.add(label);
                labels.push(label);
            }
        }
    }

    if (labels.length === 0) return null;

    const datasets = seriesEntries.map(([seriesName, points]) => ({
        label: seriesName,
        data: labels.map((label) => toNumber(points[label])),
    }));

    return { type, title, labels, datasets };
};

/** e.g. data: { "2026-03-09 to 2026-03-15": 1140, "2026-03-16 to 2026-03-22": 1161 } */
const normalizeFlatMapChart = (
    data: Record<string, unknown>,
    type: 'bar_chart' | 'line_chart',
    title?: string,
    seriesLabel?: string,
): VisualizationBarChart | VisualizationLineChart | null => {
    const entries = Object.entries(data).filter(([, value]) => isNumericValue(value));
    if (entries.length === 0) return null;

    return {
        type,
        title,
        labels: entries.map(([label]) => label),
        datasets: [
            {
                label: seriesLabel ?? 'Value',
                data: entries.map(([, value]) => toNumber(value)),
            },
        ],
    };
};

const normalizeObjectDataChart = (
    data: Record<string, unknown>,
    type: 'bar_chart' | 'line_chart',
    title?: string,
    seriesLabel?: string,
): VisualizationBarChart | VisualizationLineChart | null => {
    const hasNestedSeries = Object.values(data).some((value) => isRecord(value));
    if (hasNestedSeries) {
        return normalizeSeriesMapChart(data, type, title);
    }

    return normalizeFlatMapChart(data, type, title, seriesLabel);
};

const normalizeLabelsAndValuesChart = (
    parsed: Record<string, unknown>,
    type: 'bar_chart' | 'line_chart',
): VisualizationBarChart | VisualizationLineChart | null => {
    const labels = Array.isArray(parsed.labels)
        ? parsed.labels.map((label) => String(label))
        : null;
    if (!labels || labels.length === 0) return null;

    const datasetsFromField = normalizeDatasets(parsed.datasets, 'Series');
    if (datasetsFromField) {
        return { type, title: parsed.title ? String(parsed.title) : undefined, labels, datasets: datasetsFromField };
    }

    if (Array.isArray(parsed.values)) {
        return {
            type,
            title: parsed.title ? String(parsed.title) : undefined,
            labels,
            datasets: [
                {
                    label: String(parsed.metric ?? parsed.yAxis ?? 'Value'),
                    data: parsed.values.map(toNumber),
                },
            ],
        };
    }

    return null;
};

export const parseVisualizationPayload = (raw: string): ChatVisualization | null => {
    const normalized = raw.trim();
    const jsonMatch = normalized.match(/render_visuali[sz]ation\s*(\{[\s\S]*\})/i);
    const jsonSource = jsonMatch?.[1] ?? normalized;

    try {
        const parsed = JSON.parse(jsonSource) as Record<string, unknown>;
        const visualizationType = normalizeVisualizationType(parsed.type);
        if (!visualizationType) return null;

        if (visualizationType === 'table') {
            if (!Array.isArray(parsed.columns) || !Array.isArray(parsed.data)) return null;
            return {
                type: 'table',
                title: parsed.title ? String(parsed.title) : undefined,
                columns: parsed.columns.map((column) => String(column)),
                data: parsed.data.filter(isRecord) as Record<string, string | number>[],
            };
        }

        const title = parsed.title ? String(parsed.title) : undefined;

        const chartFromRows = Array.isArray(parsed.data)
            ? normalizeRowBasedChart(parsed.data, visualizationType)
            : null;
        if (chartFromRows) {
            return { ...chartFromRows, title: title ?? chartFromRows.title };
        }

        const seriesLabel = String(parsed.y_axis ?? parsed.yAxis ?? 'Value');

        const chartFromObjectData =
            isRecord(parsed.data) && !Array.isArray(parsed.data)
                ? normalizeObjectDataChart(parsed.data, visualizationType, title, seriesLabel)
                : null;
        if (chartFromObjectData) return chartFromObjectData;

        const chartFromLabels = normalizeLabelsAndValuesChart(parsed, visualizationType);
        if (chartFromLabels) return chartFromLabels;

        return null;
    } catch {
        return null;
    }
};

export const getDatasetColors = (datasetCount: number) =>
    Array.from({ length: datasetCount }, (_, index) => CHART_COLORS[index % CHART_COLORS.length]);